import ipcApiRoute from '@/constants/ipcApiRoute'
import { BusinessType, JsonDBKey, PrintType } from '@/constants'
import { SettingStorage } from '@/utils/storage'

const Renderer = (window.require && window.require('electron')) || window.electron || {}

/**
 * ipc
 * 官方api说明：https://www.electronjs.org/zh/docs/latest/api/ipc-renderer
 *
 * 属性/方法
 * ipc.invoke(channel, param) - 发送异步消息（invoke/handle 模型）
 * ipc.sendSync(channel, param) - 发送同步消息（send/on 模型）
 * ipc.on(channel, listener) - 监听 channel, 当新消息到达，调用 listener
 * ipc.once(channel, listener) - 添加一次性 listener 函数
 * ipc.removeListener(channel, listener) - 为特定的 channel 从监听队列中删除特定的 listener 监听者
 * ipc.removeAllListeners(channel) - 移除所有的监听器，当指定 channel 时只移除与其相关的所有监听器
 * ipc.send(channel, ...args) - 通过channel向主进程发送异步消息
 * ipc.postMessage(channel, message, [transfer]) - 发送消息到主进程
 * ipc.sendTo(webContentsId, channel, ...args) - 通过 channel 发送消息到带有 webContentsId 的窗口
 * ipc.sendToHost(channel, ...args) - 消息会被发送到 host 页面上的 <webview> 元素
 */

/**
 * ipc
 */
const ipc = Renderer.ipcRenderer || undefined

/** A4 纸张业务类型 */
const A4_BUSINESS_TYPES = new Set([BusinessType.PFXHD_A4, BusinessType.TEST_A4])

/**
 * 打印前将当前选中的打印机名称写入 PRINT_SETTING
 * Electron hardware.print 校验逻辑（RECEIPT 读 printerPaper，价签读 printerPrice）：
 *   const printerName = args.printType == 'RECEIPT' ? result[0].printerPaper : result[0].printerPrice
 * startPrint 同样使用 printerPaper / printerPrice 作为 deviceName
 */
async function syncPrintSettingBeforePrint(printArgs = {}) {
  const setting = SettingStorage.get()
  if (!setting || !ipc?.invoke) return

  const { businessType, printType } = printArgs
  const printSetting = { ...setting }

  if (printType === PrintType.PRICE_TAG) {
    // 价签：写入价签打印机名称
    printSetting.printerPrice = setting.printerPrice
  } else if (A4_BUSINESS_TYPES.has(businessType)) {
    // 仅切换打印机：配置了 A4 打印机则用 A4，否则沿用默认小票打印机
    if (setting.printerA4) {
      printSetting.printerPaper = setting.printerA4
    }
    // paperSize / landscape 由 print.html 按 PFXHD_A4 业务类型处理，勿写入 PRINT_SETTING
  } else {
    // 普通小票：写入小票打印机名称
    printSetting.printerPaper = setting.printerPaper
  }
  console.log('TIPS: printSetting:', printSetting)
  console.log('TIPS: printSetting打印机名称:', printSetting.printerPaper)

  await ipc.invoke(ipcApiRoute.dbExec, {
    action: 'clear',
    key: JsonDBKey.PRINT_SETTING,
  })
  await ipc.invoke(ipcApiRoute.dbExec, {
    action: 'add',
    key: JsonDBKey.PRINT_SETTING,
    value: printSetting,
  })
}

if (ipc?.send) {
  const rawSend = ipc.send.bind(ipc)
  ipc.send = (channel, ...args) => {
    if (channel === ipcApiRoute.print) {
      const printArgs = args[0] || {}
      syncPrintSettingBeforePrint(printArgs)
        .then(() => {
          rawSend(channel, ...args)
          console.log('TIPS: ipc.send', channel, ...args)
        })
        .catch((error) => {
          console.error('TIPS: syncPrintSettingBeforePrint error:', error)
          rawSend(channel, ...args)
        })
      return
    }
    rawSend(channel, ...args)
    console.log('TIPS: ipc.send', channel, ...args)
  }
}

/**
 * 是否为EE环境
 */
const isEE = ipc ? true : false

export { Renderer, ipc, isEE }
